import {
  Dialog,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  Chip,
  TextField,
} from '@mui/material';
import ButtonWithSpinner from 'src/components/buttons/button-with-spinner';
import { AppDispatch } from '@/src/store';
import { useEffect, useState } from 'react';
import { IoCloseCircleOutline } from 'react-icons/io5';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { IUserDetails } from '../../../common/types/user.model';
import {
  fetchListOfSharedWithUsers,
  fetchListOfUnsharedWithUsers,
  removeScenarioShareAccess,
  shareScenario
} from '../../api/scenario-manager-api';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import UserListAutoComplete from '../user-share-list/user-share-list';
import { addScenarioManagerComment } from '../../scenario-manager-slice';
import { updateNoteInState} from '../../../common/features/table/store/note-slice';
import { updateScenarioManagerNote } from '../../scenario-manager-slice';



interface IShareScenarioDialogProps {
  /**
   * Controls visibility of dialog
   */
  isOpen: boolean;
  /**
   * Close handler for scenario close action
   * @returns void
   */
  onClose: (isShared?: boolean) => void;
}

function UserChip({
  user,
  handleDelete
}: {
  user: IUserDetails;
  handleDelete?: (selectedUser: IUserDetails) => Promise<unknown>;
}) {
  return (
    <Chip
      sx={{ mb: 1, mr: 1 }}
      onDelete={handleDelete ? () => handleDelete(user): undefined}
      label={
        <Grid container flexDirection='column' alignItems='baseline'>
          <Typography fontSize='12px' fontWeight={700}>
            {user.first_name} {user.last_name}
          </Typography>
          {user.first_name === '' && user.last_name === '' && (
            <Typography fontSize='12px' fontWeight={700}>
              {user.email}
            </Typography>
          )}
        </Grid>
      }
    />
  );
}

export default function ShareScenarioDialog({
  isOpen,
  onClose
}: IShareScenarioDialogProps) {
  const appDispatch = useAppDispatch();
  const { scenarioToShare } = useAppSelector((state) => state.scenarioManager);
  const {user} = useAppSelector((state) => state.user);
  const { sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.SUMMARY
  });
  const [sharedWithUsers, setSharedWithUsers] = useState<IUserDetails[]>([]);
  const [unsharedWithUsers, setUnSharedWithUsers] = useState<IUserDetails[]>(
    []
  );
  const [newUsers, setNewUsers] = useState<IUserDetails[]>([]);
  const [totalUsers, setTotalUsers] = useState<IUserDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddUser = (option: IUserDetails): void => {
    const newList = [...newUsers, option];
    setNewUsers(newList);
    setTotalUsers([...sharedWithUsers, ...newList]);
  };
  const [note, setNote] = useState<string>('');
  //const {  currentScenario } = useAppSelector((state) => state.noteDialog);

  useEffect(() => {
    setIsLoading(true);
    fetchListOfSharedWithUsers(sourceId, scenarioToShare.scenario_id)
      .then((sharedUsers) => {
        setSharedWithUsers(sharedUsers);
        setTotalUsers(sharedUsers);
        return fetchListOfUnsharedWithUsers(
          sourceId,
          scenarioToShare.scenario_id
        ).then((unsharedUsers) => {
          setUnSharedWithUsers(unsharedUsers);
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleShareScenario = async(): Promise<void> =>{
    if(note.trim()){
       try {
              const response = await appDispatch(addScenarioManagerComment({
               sourceId: sourceId,
               scenarioId: scenarioToShare.scenario_id,
               comment_text: note.trim(),
             })).unwrap();
       
             appDispatch(updateNoteInState({
                 scenario_id: scenarioToShare.scenario_id,
                 note_text: response?.comment_text,
                 note_user: response?.comment_user_name,
                 note_updated_at: response?.created_at,
                 note_pk_id: response?.pk_id,
               }));
              
               appDispatch(updateScenarioManagerNote({
                 scenario_id: scenarioToShare.scenario_id,
                 noteData: {
                   pk_id: response?.pk_id,
                   comment_text: response?.comment_text,
                   comment_user: response?.comment_user,
                   comment_user_name: response?.comment_user_name,
                   created_at: response?.created_at,
                   updated_at: response?.updated_at || response?.created_at,
                   is_edited: false
                 }
               }));
             } catch (error) {
      console.error('Failed to send note:', error);
    }

    }
    shareScenario(
      {
        users: newUsers.map((user) => user.id)
      },
      sourceId,
      scenarioToShare.scenario_id
    ).then(() => {
      onClose(true);
    });
  }

  const handleDelete = (selectedUser: IUserDetails) => {
    const userToDeleteIndex = sharedWithUsers.findIndex(
      (user) => user.id === selectedUser.id
    );
    if (userToDeleteIndex > -1) {
      return removeScenarioShareAccess(
        {
          user_id: selectedUser.id
        },
        sourceId,
        scenarioToShare.scenario_id
      ).then(() => {
        const users = sharedWithUsers.filter(
          (user, index) => index !== userToDeleteIndex
        );
        setSharedWithUsers(users);
        setTotalUsers([...users, ...newUsers]);
      });
    }
    const newList = newUsers.filter((data) => data.id !== selectedUser.id);
    setNewUsers([...newList]);
    setTotalUsers([...newList, ...sharedWithUsers]);
    return Promise.resolve();
  };
  const isOwner = !totalUsers.some( (u) => u.id === user )
  return (
    <Dialog maxWidth='sm' open={isOpen} fullWidth>
      <Grid
        p={2}
        container
        justifyContent='space-between'
        style={{ paddingBottom: '0px' }}
      >
        <Grid item xs='auto'>
          <Typography
            color='primary'
            fontWeight={700}
            style={{ paddingTop: '8px' }}
          >
            Share Scenario
          </Typography>
        </Grid>
        <Grid item xs='auto'>
          <IconButton
            aria-label='close'
            size='medium'
            color='primary'
            onClick={() => onClose()}
          >
            <IoCloseCircleOutline />
          </IconButton>
        </Grid>
      </Grid>
      <DialogContent style={{ paddingTop: '0px' }}>
        <Grid container>
          <Grid item xs={12} my={1}>
            <Typography fontSize='12px' color='#777777'>
              People registered on platform can view scenario. To share scenario
              enter the name of the user in the text field below.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {totalUsers.map((user) => (
              <UserChip user={user} 
              handleDelete={isOwner? handleDelete: undefined} /> // Only owner has delete icon
            ))}
          </Grid>
          {isLoading ? (
            <Grid item xs={12}>
              <LinearProgress />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <UserListAutoComplete
                list={unsharedWithUsers}
                onAdd={handleAddUser}
                value={totalUsers}
              />
            </Grid>
          )}
        </Grid>
        
      </DialogContent>
      <TextField
         onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            placeholder="Type your Note here..."
            className="text-field"
            color="primary"
            sx={{ margin: '16px' }}
            
          />
      <DialogActions>
        <Grid container justifyContent='flex-end'>
          <ButtonWithSpinner
            disabled={!newUsers.length}
            onClick={handleShareScenario}
          >
            Share Scenario
          </ButtonWithSpinner>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
